import { createRouter, createWebHistory } from "vue-router";
import MainLayout from "../layouts/MainLayout.vue";
import authService from "../services/AuthService";
import LoginView from "../views/LoginView.vue";
import LogoutView from "../views/LogoutView.vue";
import LoginCallbackView from "../views/LoginCallbackView.vue";
import SelectBrandView from "../views/SelectBrandView.vue";

import store from "../store";
import { useBreadcrumb } from "../composables/useBreadcrumb.js";
import { is_authenticated } from "./navigationGuard.js";
import { isRouteAllowed } from "../composables/useRoles.js";
import { ensureCapabilitiesLoaded } from "../composables/useCapabilities.js";
import { ensureSelectedBrandLoaded } from "../composables/useSelectedBrand.js";
import { playModeForPath } from "../config/menu.js";

const routes = [
    {
        path: "/",
        component: MainLayout,
        children: [
            {
                path: "/",
                name: "dashboard",
                component: () => import("../views/DashboardView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/users",
                name: "UserListView",
                component: () => import("../views/Users/UserListView.vue"),
                meta: {
                    requires_auth: true
                }
            },

            {
                path: "/users/new",
                name: "UserFormView",
                component: () => import("../views/Users/UserFormView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/users/:id/edit",
                name: "UserFormView",
                component: () => import("../views/Users/UserFormView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/affiliates",
                name: "AffiliateListView",
                component: () => import("../views/Affiliates/AffiliateListView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/affiliates/:id",
                name: "AffiliateDetailView",
                component: () => import("../views/Affiliates/AffiliateDetailView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/affiliates/:id/edit",
                name: "AffiliateFormView",
                component: () => import("../views/Affiliates/AffiliateFormView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/affiliates/new",
                name: "AffiliateFormView",
                component: () => import("../views/Affiliates/AffiliateFormView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/affiliates/bonus-codes",
                name: "BonusCodeListView",
                component: () => import("../views/Affiliates/BonusCodeListView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/player-targeting",
                name: "PlayerTargetingListView",
                component: () => import("../views/PlayerTargeting/PlayerTargetingListView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/player-targeting/:id",
                name: "PlayerTargetingDetailView",
                component: () => import("../views/PlayerTargeting/PlayerTargetingDetailView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/player-targeting/:id/edit",
                name: "PlayerTargetingFormView",
                component: () => import("../views/PlayerTargeting/PlayerTargetingFormView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/player-targeting/new",
                name: "PlayerTargetingFormView",
                component: () => import("../views/PlayerTargeting/PlayerTargetingFormView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/purchases",
                name: "PurchaseQueueListView",
                component: () => import("../views/Purchases/PurchaseQueueListView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/redemptions",
                name: "RedemptionQueueListView",
                component: () => import("../views/Redemptions/RedemptionQueueListView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/deposits",
                name: "DepositsQueueListView",
                component: () => import("../views/Deposits/DepositsQueueListView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/credit-purchases",
                name: "CreditPurchasesQueueListView",
                component: () => import("../views/CreditPurchases/CreditPurchasesQueueListView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/withdrawals",
                name: "WithdrawalsQueueListView",
                component: () => import("../views/Withdrawals/WithdrawalsQueueListView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/inactivity-fees",
                name: "InactivityFeesListView",
                component: () => import("../views/InactivityFees/InactivityFeesListView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/payout-review",
                name: "PayoutReviewListView",
                component: () => import("../views/PayoutReview/PayoutReviewListView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/verifications",
                name: "VerificationQueueListView",
                component: () => import("../views/Verifications/VerificationQueueListView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/amoe",
                name: "AMOEQueueListView",
                component: () => import("../views/AMOE/AMOEQueueListView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/anti-fraud",
                name: "AntiFraudQueueListView",
                component: () => import("../views/AntiFraud/AntiFraudQueueListView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/desync-queue",
                name: "DesyncQueueListView",
                component: () => import("../views/DesyncQueue/DesyncQueueListView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            // {
            //     path: "/customer-support",
            //     name: "CustomerSupportQueueListView",
            //     component: () => import("../views/CustomerSupport/CustomerSupportQueueListView.vue"),
            //     meta: {
            //         requires_auth: true
            //     }
            // }, // Commented out for MVP - may return in future
            {
                path: "/popups",
                name: "PopupListView",
                component: () => import("../views/PopupManagement/PopupListView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/popups/queue/:id",
                name: "PopupQueueDetailView",
                component: () => import("../views/PopupManagement/PopupQueueDetailView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/popups/queue/:id/entry/:entryId",
                name: "PopupEntryDetailView",
                component: () => import("../views/PopupManagement/PopupEntryDetailView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/popups/queue/:id/entry/new",
                name: "PopupEntryFormView",
                component: () => import("../views/PopupManagement/PopupEntryFormView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/carousels",
                name: "CarouselListView",
                component: () => import("../views/CarouselManagement/CarouselListView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/carousels/new",
                name: "CarouselCreateView",
                component: () => import("../views/CarouselManagement/CarouselFormView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/carousels/placement",
                name: "CarouselPlacementView",
                component: () => import("../views/CarouselManagement/CarouselDetailView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/carousels/:id",
                name: "CarouselDetailView",
                component: () => import("../views/CarouselManagement/CarouselFormView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/legal-documents",
                name: "LegalDocumentListView",
                component: () => import("../views/LegalDocuments/LegalDocumentListView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/legal-documents/type/:type",
                name: "LegalDocumentTypeDetailView",
                component: () => import("../views/LegalDocuments/LegalDocumentTypeDetailView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/legal-documents/version/:id",
                name: "LegalDocumentVersionDetailView",
                component: () => import("../views/LegalDocuments/LegalDocumentVersionDetailView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/sitewide-notifications",
                name: "SitewideNotificationListView",
                component: () => import("../views/SitewideNotifications/SitewideNotificationListView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/sitewide-notifications/:id",
                name: "SitewideNotificationDetailView",
                component: () => import("../views/SitewideNotifications/SitewideNotificationDetailView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/sitewide-notifications/:id/edit",
                name: "SitewideNotificationFormView",
                component: () => import("../views/SitewideNotifications/SitewideNotificationFormView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/sitewide-notifications/new",
                name: "SitewideNotificationFormView",
                component: () => import("../views/SitewideNotifications/SitewideNotificationFormView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            // Quest Routes
            {
                path: "/quests",
                name: "QuestScheduleListView",
                component: () => import("../views/Quests/QuestScheduleListView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/quests/schedule/new",
                name: "QuestScheduleFormView",
                component: () => import("../views/Quests/QuestScheduleFormView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/quests/schedule/:id",
                name: "QuestScheduleDetailView",
                component: () => import("../views/Quests/QuestScheduleDetailView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/quests/schedule/:id/edit",
                name: "QuestScheduleEditView",
                component: () => import("../views/Quests/QuestScheduleFormView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/quests/schedule/:scheduleId/quest/new",
                name: "QuestCreateFromScheduleView",
                component: () => import("../views/Quests/QuestFormView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/quests/schedule/:scheduleId/quest/:id",
                name: "QuestDetailFromScheduleView",
                component: () => import("../views/Quests/QuestDetailView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/quests/new",
                name: "QuestFormView",
                component: () => import("../views/Quests/QuestFormView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/quests/:id",
                name: "QuestDetailView",
                component: () => import("../views/Quests/QuestDetailView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/quests/:id/edit",
                name: "QuestEditView",
                component: () => import("../views/Quests/QuestFormView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/daily-bonus",
                name: "DailyBonusListView",
                component: () => import("../views/DailyBonus/DailyBonusListView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/daily-bonus/new",
                name: "DailyBonusFormView",
                component: () => import("../views/DailyBonus/DailyBonusFormView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/daily-bonus/calendar/:id",
                name: "DailyBonusDetailView",
                component: () => import("../views/DailyBonus/DailyBonusDetailView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/daily-bonus/entry/:id",
                name: "DailyBonusEntryDetailView",
                component: () => import("../views/DailyBonus/DailyBonusEntryDetailView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/daily-bonus/calendar/:calendarId/day/:entryId",
                name: "DailyBonusEntryDetailView",
                component: () => import("../views/DailyBonus/DailyBonusEntryDetailView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            // Segmentation Routes
            {
                path: "/segments",
                name: "SegmentListView",
                component: () => import("../views/Segmentation/SegmentListView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/segments/:id",
                name: "SegmentDetailView",
                component: () => import("../views/Segmentation/SegmentDetailView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            // Promotion Routes
            {
                path: "/promotions",
                name: "PromotionsListView",
                component: () => import("../views/Promotions/PromotionsListView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/promotions/:id",
                name: "PromotionDetailView",
                component: () => import("../views/Promotions/PromotionDetailView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/promotions/new",
                name: "PromotionCreateView",
                component: () => import("../views/Promotions/PromotionsFormView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/promotions/:id/edit",
                name: "PromotionEditView",
                component: () => import("../views/Promotions/PromotionDetailView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            // Store Packages Routes
            {
                path: "/store-packages",
                name: "StorePackageListView",
                component: () => import("../views/StorePackages/StorePackageListView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/store-packages/:id",
                name: "StorePackageDetailView",
                component: () => import("../views/StorePackages/StorePackageDetailView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/store-packages/new",
                name: "StorePackageFormView",
                component: () => import("../views/StorePackages/StorePackageFormView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/store-packages/:id/edit",
                name: "StorePackageEditView",
                component: () => import("../views/StorePackages/StorePackageFormView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            // LUCKY_WHEEL_DISABLED: Lucky Wheel Spin Config Routes
            // {
            //     path: "/lucky-wheel-spin-configs",
            //     name: "LuckyWheelSpinConfigListView",
            //     component: () => import("../views/LuckyWheelSpins/LuckyWheelSpinConfigListView.vue"),
            //     meta: {
            //         requires_auth: true
            //     }
            // },
            // {
            //     path: "/lucky-wheel-spin-configs/:id",
            //     name: "LuckyWheelSpinConfigDetailView",
            //     component: () => import("../views/LuckyWheelSpins/LuckyWheelSpinConfigDetailView.vue"),
            //     meta: {
            //         requires_auth: true
            //     }
            // },
            // Promo Credit Template Routes
            {
                path: "/promo-credit-templates",
                name: "PromoCreditTemplateListView",
                component: () => import("../views/PromoCreditTemplates/PromoCreditTemplateListView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/promo-credit-templates/create",
                name: "PromoCreditTemplateFormView",
                component: () => import("../views/PromoCreditTemplates/PromoCreditTemplateFormView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/promo-credit-templates/:id",
                name: "PromoCreditTemplateDetailView",
                component: () => import("../views/PromoCreditTemplates/PromoCreditTemplateDetailView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            // Free Spin Campaigns Routes
            // Purchase Offers Routes
            {
                path: "/purchase-offers",
                name: "PurchaseOfferListView",
                component: () => import("../views/PurchaseOffers/PurchaseOfferListView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/purchase-offers/:id",
                name: "PurchaseOfferDetailView",
                component: () => import("../views/PurchaseOffers/PurchaseOfferDetailView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/purchase-offers/new",
                name: "PurchaseOfferFormView",
                component: () => import("../views/PurchaseOffers/PurchaseOfferFormView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/purchase-offers/:id/edit",
                name: "PurchaseOfferEditView",
                component: () => import("../views/PurchaseOffers/PurchaseOfferFormView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            // Amwest Credit Pack Routes
            {
                path: "/amwest-credit-packs",
                name: "AmwestCreditPackListView",
                component: () => import("../views/AmwestCreditPacks/AmwestCreditPackListView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/amwest-credit-packs/:id",
                name: "AmwestCreditPackDetailView",
                component: () => import("../views/AmwestCreditPacks/AmwestCreditPackDetailView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/games",
                name: "GameListView",
                component: () => import("../views/Games/GameListView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/games/:id",
                name: "GameDetailView",
                component: () => import("../views/Games/GameDetailView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/game-catalogs",
                name: "GameCatalogListView",
                component: () => import("../views/Games/GameCatalogListView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/game-catalogs/:id",
                name: "GameCatalogDetailView",
                component: () => import("../views/Games/GameCatalogDetailView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/game-categories",
                name: "GameCategoryListView",
                component: () => import("../views/Games/GameCategoryListView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/game-categories/:id",
                name: "GameCategoryDetailView",
                component: () => import("../views/Games/GameCategoryDetailView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/game-placement-editor",
                name: "GamePlacementVisualEditorView",
                component: () => import("../views/Games/GamePlacementVisualEditorView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/all-games-order",
                name: "AllGamesOrderView",
                component: () => import("../views/Games/AllGamesOrderView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/security/banned-ips",
                name: "BannedIPsView",
                component: () => import("../views/Security/BannedIPsView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/security/banned-devices",
                name: "BannedDevicesView",
                component: () => import("../views/Security/BannedDevicesView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/security/banned-domains",
                name: "BannedDomainsView",
                component: () => import("../views/Security/BannedDomainsView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/compliance/global-exclusions",
                name: "GlobalExclusionsView",
                component: () => import("../views/GlobalExclusions/GlobalExclusionsView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/global-configurations",
                name: "GlobalConfigurationView",
                component: () => import("../views/GlobalConfigurations/GlobalConfigurationView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/loyalty-levels",
                name: "LoyaltyLevelListView",
                component: () => import("../views/LoyaltyLevels/LoyaltyLevelListView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/loyalty-levels/:id",
                name: "LoyaltyLevelDetailView",
                component: () => import("../views/LoyaltyLevels/LoyaltyLevelDetailView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/loyalty-levels/new",
                name: "LoyaltyLevelFormView",
                component: () => import("../views/LoyaltyLevels/LoyaltyLevelFormView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/loyalty-levels/:id/edit",
                name: "LoyaltyLevelEditView",
                component: () => import("../views/LoyaltyLevels/LoyaltyLevelFormView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/refer-a-friend",
                name: "ReferAFriendView",
                component: () => import("../views/ReferAFriend/ReferAFriendView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/game-providers",
                name: "GameProviderListView",
                component: () => import("../views/GameProviders/GameProviderListView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/game-providers/:id",
                name: "GameProviderDetailView",
                component: () => import("../views/GameProviders/CasinoGovernanceOwnerDetailView.vue"),
                meta: {
                    requires_auth: true,
                    governanceScope: "provider"
                }
            },
            {
                path: "/game-studios",
                name: "GameStudioListView",
                component: () => import("../views/GameProviders/GameStudioListView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/game-studios/:id",
                name: "GameStudioDetailView",
                component: () => import("../views/GameProviders/CasinoGovernanceOwnerDetailView.vue"),
                meta: {
                    requires_auth: true,
                    governanceScope: "studio"
                }
            },
            {
                path: "/payment-providers",
                name: "PaymentProviderListView",
                component: () => import("../views/PaymentProviders/PaymentProviderListView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/payment-providers/:id",
                name: "PaymentProviderDetailView",
                component: () => import("../views/PaymentProviders/PaymentProviderDetailView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/xtremepush",
                name: "CustomerIOView",
                component: () => import("../views/CustomerIO/CustomerIOView.vue"),
                meta: {
                    requires_auth: true
                }
            }, // Customer.io integration (keeping /xtremepush route for backward compatibility)
            {
                path: "/postbacks",
                name: "PostbackListView",
                component: () => import("../views/Postbacks/PostbackListView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/postbacks/:id",
                name: "PostbackDetailView",
                component: () => import("../views/Postbacks/PostbackDetailView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/postbacks/:id/edit",
                name: "PostbackFormView",
                component: () => import("../views/Postbacks/PostbackFormView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/postbacks/new",
                name: "PostbackFormView",
                component: () => import("../views/Postbacks/PostbackFormView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            // CS Portal Routes
            {
                path: "/cs-portal",
                name: "CSPortalListView",
                component: () => import("../views/CSPortal/CSPortalListView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/cs-portal/submit",
                name: "CSPortalSubmitView",
                component: () => import("../views/CSPortal/CSPortalSubmitView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/cs-portal/:id",
                name: "CSPortalDetailView",
                component: () => import("../views/CSPortal/CSPortalDetailView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/deploy-dashboard",
                name: "DeployDashboardView",
                component: () => import("../views/DeployDashboard/DeployDashboardView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/platform-health",
                name: "PlatformHealthView",
                component: () => import("../views/PlatformHealth/PlatformHealthView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/admin/maintenance",
                name: "MaintenanceView",
                component: () => import("../views/Admin/MaintenanceView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/admin/feature-flags",
                name: "FeatureFlagListView",
                component: () => import("../views/Admin/FeatureFlagListView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/admin/bulk-users",
                name: "BulkUsersView",
                component: () => import("../views/Admin/BulkUsersView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            // {
            //     path: "/admin/users",
            //     name: "AdminUserListView",
            //     component: () => import("../views/Admin/AdminUserListView.vue"),
            //     meta: {
            //         requires_auth: true
            //     }
            // },
            // {
            //     path: "/admin/users/:id",
            //     name: "AdminUserDetailView",
            //     component: () => import("../views/Admin/AdminUserDetailView.vue"),
            //     meta: {
            //         requires_auth: true
            //     }
            // },
            // {
            //     path: "/admin/users/:id/edit",
            //     name: "AdminUserFormView",
            //     component: () => import("../views/Admin/AdminUserFormView.vue"),
            //     meta: {
            //         requires_auth: true
            //     }
            // },
            // {
            //     path: "/admin/users/new",
            //     name: "AdminUserFormView",
            //     component: () => import("../views/Admin/AdminUserFormView.vue"),
            //     meta: {
            //         requires_auth: true
            //     }
            // },
            {
                path: "/admin/roles",
                name: "AdminRoleListView",
                component: () => import("../views/Admin/AdminRoleListView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            // {
            //     path: "/admin/roles/:id",
            //     name: "AdminRoleDetailView",
            //     component: () => import("../views/Admin/AdminRoleDetailView.vue"),
            //     meta: {
            //         requires_auth: true
            //     }
            // },
            // {
            //     path: "/admin/roles/:id/edit",
            //     name: "AdminRoleFormView",
            //     component: () => import("../views/Admin/AdminRoleFormView.vue"),
            //     meta: {
            //         requires_auth: true
            //     }
            // },
            // {
            //     path: "/admin/roles/new",
            //     name: "AdminRoleFormView",
            //     component: () => import("../views/Admin/AdminRoleFormView.vue"),
            //     meta: {
            //         requires_auth: true
            //     }
            // },
            // {
            //     path: "/admin/audit-history",
            //     name: "AdminHistoryDetailView",
            //     component: () => import("../views/Admin/AdminHistoryDetailView.vue"),
            //     meta: {
            //         requires_auth: true
            //     }
            // }, // Commented out for MVP - may return in future
            {
                path: "/users/:id",
                name: "UserDetailView",
                component: () => import("../views/Users/UserDetailView.vue"),
                meta: {
                    requires_auth: true
                }
            },
            {
                path: "/settings",
                name: "SettingsView",
                component: () => import("../views/SettingsView.vue"),
                meta: {
                    requires_auth: true
                }
            }
        ]
    },
    {
        path: "/login",
        name: "login",
        component: LoginView,
        meta: {
            requires_auth: false
        }
    },
    {
        path: "/login/callback",
        name: "loginCallback",
        component: LoginCallbackView,
        meta: {
            requires_auth: false
        }
    },
    {
        path: "/logout",
        name: "logout",
        component: LogoutView,
        meta: {
            requires_auth: false
        }
    },
    {
        // Backend-driven brand picker (#2122, #2230). Full-screen, login-style
        // page; requires an authenticated admin. Reached on demand from the user
        // profile menu; NOT forced after login (the admin logs straight into their
        // last-used brand).
        path: "/select-brand",
        name: "selectBrand",
        component: SelectBrandView,
        meta: {
            requires_auth: true
        }
    }
];

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes
});

router.beforeEach(async (to, from, next) => {
    // Routes that require authentication
    if (to.meta.requires_auth) {
        if (!await is_authenticated()) {
            console.log(`🔒 Route ${to.name} requires authentication, redirecting to login`);
            next({ name: "login" });
            return;
        }

        // Ensure the admin's capabilities are loaded before the access check so
        // a deep link / hard refresh is gated correctly (idempotent, never throws).
        await ensureCapabilitiesLoaded();

        // Ensure the selected brand is hydrated from the backend on a hard refresh
        // / deep link, so the X-Brand-Code header matches the backend-owned session
        // brand before any view fetches (idempotent, never throws) (#2122).
        await ensureSelectedBrandLoaded();

        // Capability-based access control check
        if (!isRouteAllowed(to.path)) {
            console.log(`🚫 Access denied to ${to.path} - insufficient permissions`);
            // Redirect to dashboard (which is in Home section, accessible to all)
            next({ name: "dashboard" });
            return;
        }

        // Play-mode gating: a route owned by one play mode must not be reachable
        // under a brand of the other (e.g. sweeps /purchases on a real-money brand),
        // even via a deep link or bookmark that bypasses the menu filter (#2158).
        // Only enforce when the brand's play mode is known; an unknown mode falls
        // back to the capability check above rather than blocking navigation.
        const routePlayMode = playModeForPath(to.path);
        const brandPlayMode = store.state.selectedBrand?.playMode;
        if (routePlayMode && brandPlayMode && routePlayMode !== brandPlayMode) {
            console.log(`🚫 ${to.path} is ${routePlayMode}-only; selected brand is ${brandPlayMode}`);
            next({ name: "dashboard" });
            return;
        }
    }

    // Redirect authenticated users away from login page
    if (to.name === "login" && await is_authenticated()) {
        console.log("👤 User already authenticated, redirecting to dashboard");
        next({ name: "dashboard" });
        return;
    }

    next();
});

router.afterEach((to, from) => {
    // Clear breadcrumb on route change
    if (to.name !== "login" && to.name !== "logout" && to.name !== from.name) {
        const { clearBreadcrumb } = useBreadcrumb();
        clearBreadcrumb();
    }

    // // Track recently visited pages
    // if (
    //   to.name &&
    //   to.name !== "login" &&
    //   to.name !== "logout" &&
    //   to.name !== from.name
    // ) {
    //   const { addVisitedLink } = useRecentlyVisited();
    //   addVisitedLink(to);
    // }
});

export default router;
